import {
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTION_NAMES } from '@/lib/collections';
import { Logger } from '@/lib/logger';
import { getMonthsAgoString, THREE_MONTHS, TWELVE_MONTHS } from '@/shared/utils/dateUtils';
import type { WorkflowPeopleData } from '@/shared/types/intel/workflowPeople';
import { classifyRole } from '@/shared/classifiers/roles/classifyRole';
import { classifySeniorityBand } from '@/shared/classifiers/roles/classifySeniorityBand';
import { classifyWorkflowRole } from '@/shared/classifiers/roles/classifyWorkflowRole';

const logger = Logger.createScoped('TeamMemberDataService');

const COLLECTION_NAME = COLLECTION_NAMES.INTEL_CORE_TEAM_MEMBERS;

/**
 * Team member data structure from intel_core_team_members collection
 */
export interface TeamMemberData {
  user_id: string;
  user_email: string;
  month: string; // from month.value
  role_category: string; // "Lead", "Manager", "Strategist", "Specialist"
  job_title: string;
  network_size: number;
  network_team_members: string[]; // Array of user IDs
  // Spend by channel
  search_spend: number;
  social_spend: number;
  shopping_spend: number;
  other_google_spend: number;
  display_video_native_audio_spend: number;
  uncategorized_spend: number;
  // Spend by industry
  finance_spend: number;
  media_spend: number;
  retail_spend: number;
  technology_spend: number;
  other_industry_spend: number;
}

/**
 * Parse team member data from Firestore document
 */
function parseTeamMemberData(doc: any): TeamMemberData | null {
  try {
    const data = doc.data();

    // Ensure required fields exist
    if (!data.user_id || !data.month?.value) {
      return null;
    }

    return {
      user_id: data.user_id || '',
      user_email: data.user_email || '',
      month: data.month.value,
      role_category: data.role_category || '',
      job_title: data.job_title || '',
      network_size: parseInt(data.network_size) || 0,
      network_team_members: Array.isArray(data.network_team_members) ? data.network_team_members : [],
      search_spend: parseFloat(data.search_spend) || 0,
      social_spend: parseFloat(data.social_spend) || 0,
      shopping_spend: parseFloat(data.shopping_spend) || 0,
      other_google_spend: parseFloat(data.other_google_spend) || 0,
      display_video_native_audio_spend: parseFloat(data.display_video_native_audio_spend) || 0,
      uncategorized_spend: parseFloat(data.uncategorized_spend) || 0,
      finance_spend: parseFloat(data.finance_spend) || 0,
      media_spend: parseFloat(data.media_spend) || 0,
      retail_spend: parseFloat(data.retail_spend) || 0,
      technology_spend: parseFloat(data.technology_spend) || 0,
      other_industry_spend: parseFloat(data.other_industry_spend) || 0,
    };
  } catch (error) {
    logger.error('Error parsing team member data:', error);
    return null;
  }
}

/**
 * ============================================================================
 * LAYER 1: RAW DATA FETCHING
 * ============================================================================
 */

/**
 * Fetch unique team members active in the most recent available month(s).
 *
 * Months are resolved from the data itself (the latest `month.value` present),
 * not the calendar, so a not-yet-collected current month never blanks the list.
 *
 * @param monthsToInclude - How many of the most recent data months to draw from.
 *   Defaults to 1 (latest month only). Pass 2 to include anyone active in either
 *   of the last two months, which avoids dropping employees who were out for a
 *   single month. Each user is deduped to their most recent record.
 * @returns Array of unique team member data
 */
export async function fetchAllTeamMembers(monthsToInclude: number = 1): Promise<TeamMemberData[]> {
  try {

    const threeMonthsAgoString = getMonthsAgoString(THREE_MONTHS);

    const q = query(
      collection(db, COLLECTION_NAME),
      where('month.value', '>=', threeMonthsAgoString)
    );

    const querySnapshot = await getDocs(q);

    logger.info(`Fetched ${querySnapshot.size} documents from Firestore`);

    // First pass: find the most recent `monthsToInclude` distinct months present
    const monthValues = new Set<string>();
    querySnapshot.forEach((doc) => {
      const value = doc.data().month?.value;
      if (value) monthValues.add(value);
    });

    const recentMonths = new Set(
      Array.from(monthValues)
        .sort((a, b) => b.localeCompare(a))
        .slice(0, monthsToInclude)
    );

    logger.info(`Including months: ${Array.from(recentMonths).join(', ')}`);

    // Second pass: collect unique members across those months, keeping each
    // user's most recent record so role/email stay current
    const teamMembersMap = new Map<string, TeamMemberData>();
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (!data.month?.value || !recentMonths.has(data.month.value)) return;

      const parsed = parseTeamMemberData(doc);
      if (!parsed) return;

      const existing = teamMembersMap.get(parsed.user_id);
      if (!existing || parsed.month > existing.month) {
        teamMembersMap.set(parsed.user_id, parsed);
      }
    });

    const teamMembers = Array.from(teamMembersMap.values()).sort((a, b) =>
      a.user_email.localeCompare(b.user_email)
    );

    logger.info(`Returning ${teamMembers.length} unique team members`);
    logger.info(`Team members range: ${teamMembers[0]?.user_email} to ${teamMembers[teamMembers.length - 1]?.user_email}`);

    return teamMembers;
  } catch (error) {
    logger.error('Error fetching all team members:', error);
    throw error;
  }
}

/**
 * Fetch team member data for a specific user across time
 * Returns all data points from the last 12 months
 *
 * @param userId - The user ID to fetch data for
 * @returns Array of team member data points for the user
 */
export async function fetchTeamMemberData(userId: string): Promise<TeamMemberData[]> {
  try {

    const twelveMonthsAgoString = getMonthsAgoString(TWELVE_MONTHS);

    const q = query(
      collection(db, COLLECTION_NAME),
      where('user_id', '==', userId),
      where('month.value', '>=', twelveMonthsAgoString)
    );

    const querySnapshot = await getDocs(q);

    const userData: TeamMemberData[] = [];

    querySnapshot.forEach((doc) => {
      const parsed = parseTeamMemberData(doc);
      if (parsed) {
        userData.push(parsed);
      }
    });

    // Sort by month descending (newest first)
    userData.sort((a, b) => b.month.localeCompare(a.month));

    return userData;
  } catch (error) {
    logger.error('Error fetching team member data:', error);
    throw error;
  }
}

/**
 * ============================================================================
 * LAYER 2: ENRICHMENT FUNCTIONS
 * ============================================================================
 */


/**
 * Complexity breakdown for a user
 */
interface ComplexityBreakdown {
  high_fte: number;
  medium_fte: number;
  low_fte: number;
  high_percentage: number;
  medium_percentage: number;
  low_percentage: number;
}

/**
 * Calculate complexity breakdown for a user based on their workflow assignments
 * Requires workflow data to be provided for complexity information
 *
 * @param userWorkflowData - User's workflow people data
 * @param workflowComplexityMap - Map of workflow_id to complexity level
 * @returns Complexity breakdown with FTE and percentages
 */
export function calculateComplexityBreakdown(
  userWorkflowData: WorkflowPeopleData[],
  workflowComplexityMap: Map<string, string>
): ComplexityBreakdown {
  const breakdown = {
    high_fte: 0,
    medium_fte: 0,
    low_fte: 0,
    high_percentage: 0,
    medium_percentage: 0,
    low_percentage: 0,
  };

  // Sum FTE by complexity
  userWorkflowData.forEach((wpData) => {
    const complexity = workflowComplexityMap.get(wpData.workflow_id) || '';
    const fte = wpData.user_workflow_fte;

    if (complexity === 'High') {
      breakdown.high_fte += fte;
    } else if (complexity === 'Medium') {
      breakdown.medium_fte += fte;
    } else if (complexity === 'Low') {
      breakdown.low_fte += fte;
    }
  });

  // Calculate percentages
  const totalFte = breakdown.high_fte + breakdown.medium_fte + breakdown.low_fte;
  if (totalFte > 0) {
    breakdown.high_percentage = (breakdown.high_fte / totalFte) * 100;
    breakdown.medium_percentage = (breakdown.medium_fte / totalFte) * 100;
    breakdown.low_percentage = (breakdown.low_fte / totalFte) * 100;
  }

  return breakdown;
}

/**
 * Calculate role distribution for a user
 *
 * @param userWorkflowData - User's workflow people data for current month
 * @returns Object with counts by role
 */
export function calculateRoleDistribution(userWorkflowData: WorkflowPeopleData[]): {
  role_1: number;
  role_2: number;
  role_3: number;
  role_other: number;
  total: number;
} {
  const distribution = {
    role_1: 0,
    role_2: 0,
    role_3: 0,
    role_other: 0,
    total: 0,
  };

  userWorkflowData.forEach((wpData) => {
    distribution.total++;

    switch (classifyWorkflowRole(wpData.user_workflow_role)) {
      case 'lead':
        distribution.role_1++;
        break;
      case 'support_1':
        distribution.role_2++;
        break;
      case 'support_2':
        distribution.role_3++;
        break;
      default:
        distribution.role_other++;
        break;
    }
  });

  return distribution;
}

/**
 * Calculate network metrics from team member data
 *
 * @param teamMemberData - Array of team member data
 * @returns Aggregated network metrics
 */
export function calculateNetworkMetrics(teamMemberData: TeamMemberData[]): {
  averageNetworkSize: number;
  totalUniqueConnections: number;
  networkSizeDistribution: {
    small: number; // 0-5
    medium: number; // 6-15
    large: number; // 16+
  };
} {
  if (teamMemberData.length === 0) {
    return {
      averageNetworkSize: 0,
      totalUniqueConnections: 0,
      networkSizeDistribution: { small: 0, medium: 0, large: 0 },
    };
  }

  let totalNetworkSize = 0;
  const allConnections = new Set<string>();
  const distribution = { small: 0, medium: 0, large: 0 };

  teamMemberData.forEach((member) => {
    totalNetworkSize += member.network_size;

    // Categorize network size
    if (member.network_size <= 5) {
      distribution.small++;
    } else if (member.network_size <= 15) {
      distribution.medium++;
    } else {
      distribution.large++;
    }

    // Track unique connections
    member.network_team_members.forEach((userId) => allConnections.add(userId));
  });

  return {
    averageNetworkSize: totalNetworkSize / teamMemberData.length,
    totalUniqueConnections: allConnections.size,
    networkSizeDistribution: distribution,
  };
}

/**
 * Aggregate skill spend across team members
 *
 * @param teamMemberData - Array of team member data (can be multiple months)
 * @returns Aggregated spend by channel
 */
export function aggregateSkillSpend(teamMemberData: TeamMemberData[]): {
  search: number;
  social: number;
  shopping: number;
  other_google: number;
  display_video_native_audio: number;
  uncategorized: number;
  total: number;
} {
  const totals = {
    search: 0,
    social: 0,
    shopping: 0,
    other_google: 0,
    display_video_native_audio: 0,
    uncategorized: 0,
    total: 0,
  };

  teamMemberData.forEach((member) => {
    totals.search += member.search_spend;
    totals.social += member.social_spend;
    totals.shopping += member.shopping_spend;
    totals.other_google += member.other_google_spend;
    totals.display_video_native_audio += member.display_video_native_audio_spend;
    totals.uncategorized += member.uncategorized_spend;
  });

  totals.total =
    totals.search +
    totals.social +
    totals.shopping +
    totals.other_google +
    totals.display_video_native_audio +
    totals.uncategorized;

  return totals;
}

/**
 * Aggregate industry spend across team members
 *
 * @param teamMemberData - Array of team member data (can be multiple months)
 * @returns Aggregated spend by industry
 */
export function aggregateIndustrySpend(teamMemberData: TeamMemberData[]): {
  finance: number;
  media: number;
  retail: number;
  technology: number;
  other_industry: number;
  total: number;
} {
  const totals = {
    finance: 0,
    media: 0,
    retail: 0,
    technology: 0,
    other_industry: 0,
    total: 0,
  };

  teamMemberData.forEach((member) => {
    totals.finance += member.finance_spend;
    totals.media += member.media_spend;
    totals.retail += member.retail_spend;
    totals.technology += member.technology_spend;
    totals.other_industry += member.other_industry_spend;
  });

  totals.total =
    totals.finance +
    totals.media +
    totals.retail +
    totals.technology +
    totals.other_industry;

  return totals;
}

/**
 * Calculate senior to junior ratio.
 *
 * Delegates to the shared classifier so the band definition stays consistent
 * with other seniority surfaces. Director now counts as senior (fixes a
 * silent drop that had been under-counting the most senior tier).
 *
 * @param teamMemberData - Array of team member data
 * @returns Object with senior count, junior count, and ratio
 */
export function calculateSeniorJuniorRatio(teamMemberData: TeamMemberData[]): {
  seniorCount: number;
  juniorCount: number;
  ratio: number; // senior / junior
} {
  let seniorCount = 0;
  let juniorCount = 0;

  teamMemberData.forEach((member) => {
    const band = classifySeniorityBand(classifyRole(member.role_category));
    if (band === 'senior') {
      seniorCount++;
    } else if (band === 'junior') {
      juniorCount++;
    }
  });

  return {
    seniorCount,
    juniorCount,
    ratio: juniorCount > 0 ? seniorCount / juniorCount : seniorCount,
  };
}

/**
 * ============================================================================
 * LAYER 3: CONVENIENCE FUNCTIONS
 * ============================================================================
 */

/**
 * Get the most recent month value from team member data
 *
 * @param teamMemberData - Array of team member data
 * @returns Most recent month string or empty string if no data
 */
export function getLatestMonth(teamMemberData: TeamMemberData[]): string {
  if (teamMemberData.length === 0) return '';
  return teamMemberData.reduce((latest, current) =>
    current.month > latest ? current.month : latest
  , teamMemberData[0].month);
}
